import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Download, Video } from 'lucide-react';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDeliveryInfoUrl, resolveDeliveryUrls } from '../utils/deliveryApi';

interface DeliveryInfo {
  code: string;
  expiresAtUtc: string;
  downloadFileName: string;
  apiBaseUrl?: string;
  streamUrl?: string;
  downloadUrl?: string;
}

const VideoDeliveryPublic: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<DeliveryInfo | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { streamUrl, downloadUrl } = useMemo(
    () => (code ? resolveDeliveryUrls(code, info) : { streamUrl: '', downloadUrl: '' }),
    [code, info]
  );

  useEffect(() => {
    if (!code) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getDeliveryInfoUrl(code));
        const contentType = res.headers.get('content-type') ?? '';
        if (!res.ok) {
          const body = contentType.includes('application/json')
            ? await res.json().catch(() => ({}))
            : {};
          const msg =
            (body as { error?: { message?: string } })?.error?.message ||
            (res.status === 404 ? 'This link is invalid or has expired.' : 'Could not load video.');
          if (!cancelled) setError(msg);
          return;
        }
        if (!contentType.includes('application/json')) {
          if (!cancelled) {
            setError(
              'Could not reach the video API. Rebuild Video.App with REACT_APP_API_URL set to your public Video.Api URL (e.g. https://video-api.staging.escapetheroomsa.com/api).'
            );
          }
          return;
        }
        const data = (await res.json()) as DeliveryInfo;
        if (!cancelled) {
          setInfo(data);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError('Could not reach the video server. Check that Video.Api is online and reachable.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) return;
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(downloadUrl);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
          (body as { error?: { message?: string } })?.error?.message ||
          `Download failed (${res.status}).`;
        setError(msg);
        return;
      }

      const blob = await res.blob();
      if (!blob.size) {
        setError('Downloaded file is empty.');
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = info?.downloadFileName || 'etr-video.mp4';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError('Could not download the video. Try the player menu (⋮) → Save video.');
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, info?.downloadFileName]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">Loading your video…</p>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Video unavailable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!code || !info) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700 mb-3">
            <Video className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Your escape room video</h1>
          <p className="text-gray-600 mt-1">Escape The Room SA</p>
          {info.expiresAtUtc && (
            <p className="text-sm text-gray-500 mt-2">
              Link valid until {new Date(info.expiresAtUtc).toLocaleString()}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="bg-black rounded-xl overflow-hidden shadow-lg aspect-video">
          <video
            key={streamUrl}
            src={streamUrl}
            controls
            playsInline
            className="w-full h-full"
            preload="metadata"
          >
            Your browser does not support video playback.
          </video>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            type="button"
            variant="primary"
            leftIcon={<Download className="h-4 w-4" />}
            isLoading={downloading}
            onClick={handleDownload}
          >
            Download video
          </Button>
        </div>

        <p className="text-center text-xs text-gray-500 mt-8">
          If download does not start, open the player controls and choose Save video.
        </p>
      </div>
    </div>
  );
};

export default VideoDeliveryPublic;
